import ListTemplate from "components/ListTemplate";
import moment from "moment";
import React from "react";
import { formatShortDate } from "utilities/momentutils";
import { routes } from "utilities/routing/generatedRoutes";
import Filters from "./Filters";
import { ChangeOwnerModal, ChangeStatusModal } from "components/Tables/MySelection/Modals";
import LinkedShowsList from "components/LinkedShows/LinkedShowsList";
import { Status } from "components";
import ChecklistStatus from "components/ChecklistStatus/ChecklistStatus";
import RemindedCount from "components/RemindedCount/RemindedCount";
import { mapFilterValuesToInstruction, mapResponseToFilterValues } from "./helpers";
import InvoiceLifeCyclePill from "components/Invoice/InvoiceLifeCyclePill";
import { useLoaderData } from "react-router";

const DocumentIcons = ({ invoice }: { invoice: server.dto.InvoiceForList }) => {
  return <div className="flex-column">
    {invoice.locked === "locked" && <div className="padding-right">
      <i className="icon-lock" />
    </div>}
    {invoice.isV2 && <div className="padding-right">
      <i className="icon-rocket" />
    </div>}
  </div>;
};

const DocumentNumber = ({ invoice }: { invoice: server.dto.InvoiceForList }) => {
  const href = invoice.isV1
    ? routes.invoiceV1_Detail(invoice.invoiceId).absolute
    : routes.invoiceV2_Detail(invoice.invoiceId).absolute;

  return <div className="flex-column gap-small">
    <div><a href={href}>{invoice.documentNumber}</a></div>
    <div><InvoiceLifeCyclePill value={invoice.lifeCycle.value} overDue={invoice.overDue} /></div>
  </div>;
};

const DocumentStatus = ({ invoice }: { invoice: server.dto.InvoiceForList }) => {
  if (invoice.isV1) {
    return <Status status={invoice.status} />;
  } else if (invoice.checklist) {
    return <>
      {invoice.checklist.notSet > 0 && <ChecklistStatus count={invoice.checklist.notSet} type="unset" />}
      {invoice.checklist.approved > 0 && <ChecklistStatus count={invoice.checklist.approved} type="approved" />}
      {invoice.checklist.declined > 0 && <ChecklistStatus count={invoice.checklist.declined} type="declined" />}
    </>;
  } else if (invoice.lifeCycle.value === "pending") {
    return <RemindedCount count={invoice.remindedCount} />;
  }

  return null;
};

const DueDate = ({ invoice }: { invoice: server.dto.InvoiceForList }) => {
  return <span className={invoice.overDue ? "red" : ""}>{formatShortDate(moment(invoice.dueDate))}</span>;
};

const Sales = ({ invoice }: { invoice: server.dto.InvoiceForList }) => {
  return <>
    {invoice.total.formatted}
    {invoice.total.baseCurrency !== invoice.total.currency &&
      <div className="text-sublabel">{invoice.total.formattedBaseCurrencyAmount}</div>}
  </>;
};

const Outstanding = ({ invoice }: { invoice: server.dto.InvoiceForList }) => {
  if (invoice.totalOutstanding.amount === 0 || invoice.lifeCycle.value === "draft") {
    return null;
  }

  return <span className={invoice.totalOutstanding.amount > 0 ? "red" : "blue"}>
    {invoice.totalOutstanding.formatted}
    {invoice.totalOutstanding.baseCurrency !== invoice.totalOutstanding.currency &&
      <div className="text-sublabel">{invoice.totalOutstanding.formattedBaseCurrencyAmount}</div>}
  </span>;
};

const InvoiceListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.InvoiceListFrontModel>();

  const hasInvoiceStatuses = viewModel.statuses.length > 1 && site.currentEnvironment.invoice.hasV1;

  return <>
    <ListTemplate
      title={site.currentEnvironment.invoice.plural}
      filterSet="invoice_List"
      defaultFilters={viewModel.defaultFilters}
      initialInstruction={viewModel.initialInstruction}
      fetchFunction={window.CoreApi.Invoice.Paginated}
      getId={item => item.invoiceId}
      layout={{
        type: "table",
        columns: [{
          key: "icons",
          header: "",
          render: item => <DocumentIcons invoice={item} />,
          size: "25px"
        }, {
          key: "number",
          header: RESX.GeneralLabels.Number,
          render: item => <DocumentNumber invoice={item} />,
          size: "fit-content(140px)"
        }, {
          key: "status",
          header: "",
          render: item => <DocumentStatus invoice={item} />,
          hidden: ((viewModel.statuses.length <= 1) && site.currentEnvironment.invoice.hasV1),
          size: "1fr"
        }, {
          key: "addressee",
          header: RESX.Document.Addressee,
          render: item => item.addressee.name,
          size: "1.5fr"
        }, {
          key: "send",
          header: RESX.DateTime.Send,
          render: item => `${formatShortDate(moment(item.invoiceDate))}`,
          size: "auto"
        }, {
          key: "due",
          header: RESX.DateTime.Due,
          render: item => <DueDate invoice={item} />,
          size: "auto"
        }, {
          key: "sales",
          header: RESX.Financial.Sales,
          render: item => <Sales invoice={item} />,
          hidden: !site.permissions.invoice.canReadSales,
          className: "s1_cell_financialamount",
          size: "auto"
        }, {
          key: "outstanding",
          header: RESX.Financial.Outstanding,
          render: item => <Outstanding invoice={item} />,
          hidden: !site.permissions.invoice.canReadSales,
          className: "s1_cell_financialamount",
          size: "auto"
        }, {
          key: "linkedTo",
          header: RESX.GeneralLabels.LinkedTo,
          render: item => <LinkedShowsList shows={item.linkedShows} />,
          size: "2.2fr"
        }]
      }}
      filters={<Filters initialStatuses={viewModel.statuses} />}
      functions={{
        mapFilterValuesToInstruction,
        mapResponseToFilterValues
      }}
      onEdit={item => {
        window.location.href = item.isV1
          ? routes.invoiceV1_Detail(item.invoiceId).absolute
          : routes.invoiceV2_Detail(item.invoiceId).absolute;
      }}
      options={{
        filterPreset: {
          cacheKey: "invoice_List",
          listApi: window.CoreApi.FilterPreset.ListFilterPresetsForInvoice,
          mutateApi: window.CoreApi.FilterPreset.CreateFilterPresetForInvoice
        },
        exportOptions: {
          canExportList: site.permissions.invoice.canExportList,
          exportOptionsApi: window.CoreApi.Invoice.ExportOptionsForList
        },
        mySelection: {
          mainArea: "invoice",
          isSelected: item => item.isSelected,
          batchActions:
            [hasInvoiceStatuses && {
              label: RESX.Status.ChangeStatus,
              modal: ChangeStatusModal,
              props: { statuses: viewModel.statuses }
            }, {
              label: RESX.Planning.ChangeOwner,
              modal: ChangeOwnerModal
            }].filter(Boolean)
        }
      }}
    />
  </>;
};

export default InvoiceListApp;